import React, { useEffect, useRef, useState } from 'react';
import Spline from '@splinetool/react-spline';
import classNames from 'classnames';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { default as SiteLayout } from '../components/sitelayout';
import { Container } from '../components/container';
import { graphql, Link } from 'gatsby';
import { convertUrltoCdn } from '../utils/helpers';
import { ABookDemoBtn } from '../components/abookdemo';
import { AButton } from '../components/abutton';
import { APartners } from '../components/partners/apartners';
import { ALink } from '../components/alink';
import { G2Section } from '../components/g2/section';
import { AppCarousel } from '../components/home';
import { CustomCTA } from '../components/cta/cta';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/less';
import 'swiper/less/navigation';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { Widget } from '../components/mcp/widget';
import sampleImage from '../../static/images/mcp-chat-dress.webp';

const Home = ({ data, location }) => {
  const seoTitle = data.strapiHomePage.seo_title;
  const seoDescription = data.strapiHomePage.seo_description;
  const seoKeywords = data.strapiHomePage.seo_keywords;
  const seoImage = data.strapiHomePage.seo_image;

  const percentARef = useRef(null);
  const percentBRef = useRef(null);
  const percentCRef = useRef(null);
  const bgARef = useRef(null);
  const bgBRef = useRef(null);
  const bgCRef = useRef(null);
  const productsSectionRef = useRef(null);
  const statsSectionRef = useRef(null);
  const featuresContainerRef = useRef(null);
  const image1Ref = useRef(null);
  const image2Ref = useRef(null);
  const image3Ref = useRef(null);

  const agenticAIData = [
    {
      id: 'for-productivity',
      name: 'Agentic AI for Productivity',
      chatFlow: [
        {
          from: 'user',
          text: "What's on my calendar?",
        },
        {
          from: 'ai',
          parts: [
            'I’ll help you retrieve calendar information from your Google Calendar account. First, I’ll check the available Calendar connector and then help you authenticate:',
            {
              type: 'code',
              title: 'Automation_get_credentials',
            },
          ],
        },
        {
          from: 'ai',
          parts: [
            'Great, I see you’re connected. Now, let me check available tools and tell you what meeting you have next.',
            {
              type: 'code',
              title: 'Selecting tools',
            },
            'Here’s what I found:',
            'Your first meeting is with Janet Yu at 9:30AM via Zoom.',
          ],
        },
        {
          from: 'user',
          text: 'Tell me more about Janet',
        },
        {
          from: 'ai',
          parts: [
            'Sure, I can tell you more about Janet. I’ll pull her information from your CRM.',
            {
              type: 'code',
              title: 'Connected to Hubspot',
            },
            'Here’s what I found:',
            {
              type: 'list',
              items: [
                'Name: Janet Yu',
                'Company: Best Company',
                'Title: CEO',
                'Industry: eCommerce',
                'Company Size: 500-1000',
                'Deal Status: Opportunity',
              ],
            },
          ],
        },
      ],
      integrations: [
        {
          name: 'Google Calendar',
          icon: 'https://cdn.runalloy.com/icons/googlecalendar.png',
          status: false,
        },
        {
          name: 'Hubspot',
          icon: 'https://cdn.runalloy.com/icons/hubspot.png',
          status: false,
        },
      ],
      tools: [
        {
          name: 'Calendar_events',
          status: false,
        },
        {
          name: 'Hubspot_get_contacts',
          status: false,
        },
      ],
    },
    {
      id: 'for-support',
      name: 'Agentic AI for Support',
      chatFlow: [
        {
          from: 'user',
          text: (
            <div className="flex flex-col gap-2">
              <span>Hello. I want to return this dress:</span>
              <img
                src={sampleImage}
                alt="sample"
                className="h-32 w-auto rounded object-cover sm:h-[220px]"
              />
              <span className="text-[11px] !text-[#616161]">
                http://mystore.shopify.com/dress/sku8772/...
              </span>
            </div>
          ),
        },
        {
          from: 'ai',
          parts: ['Sure. What is your order number?'],
        },
        {
          from: 'user',
          text: 'SHOP-1234',
        },
        {
          from: 'ai',
          parts: [
            'Thank you. Are you Angela?',
            {
              type: 'option',
              options: ['Yes', 'No'],
            },
          ],
        },
        {
          from: 'user',
          text: 'Yes',
        },
        {
          from: 'ai',
          parts: [
            'Please provide identification.',
            {
              type: 'button',
              label: (
                <div className="flex items-center gap-2">
                  <span>Connect with Google</span>
                  <img
                    src="https://cdn.runalloy.com/icons/google.svg"
                    alt="google"
                  />
                </div>
              ),
            },
          ],
        },
        {
          from: 'ai',
          parts: [
            'Great, I see the order. Are you sure you want to return this dress instead of exchange?',
          ],
        },
        {
          from: 'user',
          text: 'Yes',
        },
        {
          from: 'ai',
          parts: [
            'Your refund amount will be $50.99. I’m reimbursing it back to your credit card, is that okay?',
          ],
        },
        {
          from: 'user',
          text: 'Yes. Thank you',
        },
        {
          from: 'ai',
          parts: [
            'Wonderful. I’ve initiated the return, once it’s approved you will receive an email. ',
          ],
        },
      ],
      integrations: [
        {
          name: 'Shopify',
          icon: 'https://cdn.runalloy.com/icons/shopify.png',
          status: false,
        },
      ],
      tools: [
        {
          name: 'Shopify_list_users',
          status: false,
        },
        {
          name: 'Shopify_get_users',
          status: false,
        },
      ],
    },
  ];

  const products = [
    {
      id: 'mcp-registry',
      name: 'MCP Registry',
      excerpt: 'For Engineering and AI Teams',
      description:
        'Give your AI agents access to external tools through Alloy Automation’s secure, remote-hosted Model Context Protocol (MCP) infrastructure.',
      features: [
        'Access 1000+ tools',
        'Permissioning, context, & auth management',
        'Enterprise-grade security',
      ],
      cta: 'Explore MCP',
      cta_link: '/platform/mcp/',
      images: [
        'https://cdn.runalloy.com/landing/2025/home/products/mcp-registry-1.webp',
        'https://cdn.runalloy.com/landing/2025/home/products/mcp-registry-2.webp',
        'https://cdn.runalloy.com/landing/2025/home/products/mcp-registry-3.webp',
      ],
    },
    {
      id: 'embedded-ipaas',
      name: 'Embedded iPaaS',
      excerpt: 'For Product & Partnerships Teams',
      description:
        'Use the leading embedded iPaaS for fast go-lives, customized user experiences, and monitoring tools to drive adoption and expand your ecosystem without added engineering lift.',
      features: [
        'Drag-and-drop interface',
        'Workflow orchestration & data mapping',
        'Ideal for product managers',
      ],
      cta: 'Explore Embedded iPaaS',
      cta_link: '/platform/embedded/',
      images: [
        'https://cdn.runalloy.com/landing/2025/home/products/workflow-builder-1.webp',
        'https://cdn.runalloy.com/landing/2025/home/products/workflow-builder-2.webp',
        'https://cdn.runalloy.com/landing/2025/home/products/workflow-builder-3.webp',
      ],
    },
    {
      id: 'connectivity-api',
      name: 'Connectivity API',
      excerpt: 'For Product and Engineering Teams',
      description:
        'Connectivity API lets you build realtime, native, white-labeled integration UIs into your product with a single API. Access thousands of APIs instantly.',
      features: [
        'Hosted config/auth UI',
        'Fastest way to launch integrations in-product',
        'Built for SaaS',
      ],
      cta: 'Explore Connectivity API',
      cta_link: '/platform/connectivity-api/',
      images: [
        'https://cdn.runalloy.com/landing/2025/home/products/connectivity-api-1.webp',
        'https://cdn.runalloy.com/landing/2025/home/products/connectivity-api-2.webp',
        'https://cdn.runalloy.com/landing/2025/home/products/connectivity-api-4.webp',
      ],
    },
  ];

  const whyAlloy = [
    {
      name: 'AI-First',
      description: (
        <>
          The first hosted implementation of the Model <br />
          Context Protocol (MCP) — with observability, <br />
          routing, and secure execution built-in.
        </>
      ),
      icon: 'https://cdn.runalloy.com/landing/2025/home/icons/ai-bot.webp',
    },
    {
      name: 'Battle-Tested Integration Infrastructure',
      description: (
        <>
          Powering hundreds of prebuilt connectors across <br />
          commerce, accounting, finance, marketing, and more.
        </>
      ),
      icon: 'https://cdn.runalloy.com/landing/2025/home/icons/alloy.webp',
    },
    {
      name: 'Enterprise-grade from day one',
      description: (
        <>
          Alloy Automation combines low-code speed with the <br />
          security, control, and governance that modern <br />
          enterprises require. SOC 2 Type II certified, GDPR <br />
          compliant, and built for scale.
        </>
      ),
      icon: 'https://cdn.runalloy.com/landing/2025/home/icons/badges.webp',
    },
  ];

  const waysToBuild = [
    {
      name: 'APIs',
      excerpt: `Use our connectivity API to access \nhundreds of APIs.`,
      icon: 'https://cdn.runalloy.com/landing/2025/home/build-with-apis.webp',
    },
    {
      name: 'MCPs',
      excerpt: `Access hundreds of MCP servers \nwith a single connection.`,
      icon: 'https://cdn.runalloy.com/landing/2025/home/build-with-mcps.webp',
    },
    {
      name: 'Product Integrations',
      excerpt:
        'Build white label integrations fast \nwith our battle-tested iPaaS.',
      icon: 'https://cdn.runalloy.com/landing/2025/home/build-with-integrations.webp',
    },
  ];

  const commonUseCases = [
    {
      name: 'Embedded Integrations',
      excerpt: `Build truly white-labeled integrations for your product or integration marketplace.`,
      icon: 'https://cdn.runalloy.com/landing/2025/home/card-a.webp',
    },
    {
      name: 'AI Workflows',
      excerpt: `Build AI workflows without having to research the input schemas or endpoints.`,
      icon: 'https://cdn.runalloy.com/landing/2025/home/card-b.webp',
    },
    {
      name: 'AI Agent Tool Calling',
      excerpt: `Give your AI agent thousands of tools to take action in business-critical applications.`,
      icon: 'https://cdn.runalloy.com/landing/2025/home/card-c.webp',
    },
  ];

  const [selectedAgenticAIData, setSelectedAgenticAIData] = useState(0);

  // Register the ScrollTrigger plugin
  useEffect(() => {
    if (typeof window !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);

      const refreshTimeout = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);

      return () => clearTimeout(refreshTimeout);
    }
  }, []);

  // Set up scroll animation
  useEffect(() => {
    // Make sure we're in the browser and DOM is ready
    if (typeof window === 'undefined') return;

    // Allow time for component to fully mount and render
    const initTimeout = setTimeout(() => {
      // Kill any existing ScrollTrigger instances
      ScrollTrigger.getAll().forEach(st => st.kill());

      // Stats section animation
      // Set up percentage animations for all three cards with a single trigger
      if (statsSectionRef.current) {
        // Create a master timeline with ScrollTrigger
        const masterTl = gsap.timeline({
          scrollTrigger: {
            trigger: statsSectionRef.current,
            start: 'top 30%',
            once: true,
            // markers: process.env.NODE_ENV === 'development', // Show markers in development
            id: 'stats-section-animation',
          },
        });

        // Helper function to create animation for each percentage
        const createPercentAnimation = (ref, bgRef, targetValue) => {
          const element = ref.current;
          const bgElement = bgRef?.current;
          if (!element) return;

          let counter = { value: 0 };

          return gsap.to(counter, {
            value: targetValue,
            duration: 2,
            ease: 'power2.out',
            onUpdate: () => {
              const currentValue = Math.round(counter.value);
              element.textContent = `${currentValue}%`;

              // Also update background height if bgElement exists
              if (bgElement) {
                bgElement.style.height = `${currentValue}%`;
              }
            },
          });
        };

        // Add all animations to the master timeline
        // All animations will start at the same time when triggered
        masterTl
          .add(createPercentAnimation(percentARef, bgARef, 44), 0)
          .add(createPercentAnimation(percentBRef, bgBRef, 90), 0)
          .add(createPercentAnimation(percentCRef, bgCRef, 78), 0);
      }

      // Features section animation
      // Set up image switching based on scroll position within the features container
      if (featuresContainerRef.current) {
        ScrollTrigger.create({
          trigger: featuresContainerRef.current,
          start: 'top center',
          end: 'bottom center',
          // markers: process.env.NODE_ENV === 'development', // Show markers in development
          id: 'features-scroll-trigger',
          onUpdate: self => {
            // Calculate which section is currently in view based on scroll progress
            // self.progress goes from 0 (start) to 1 (end)
            if (!image1Ref.current || !image2Ref.current || !image3Ref.current)
              return;

            if (self.progress < 0.36) {
              // First section
              image1Ref.current.style.opacity = '1';
              image2Ref.current.style.opacity = '0';
              image3Ref.current.style.opacity = '0';
            } else if (self.progress < 0.7) {
              // Second section
              image1Ref.current.style.opacity = '0';
              image2Ref.current.style.opacity = '1';
              image3Ref.current.style.opacity = '0';
            } else {
              // Third section
              image1Ref.current.style.opacity = '0';
              image2Ref.current.style.opacity = '0';
              image3Ref.current.style.opacity = '1';
            }
          },
        });
      }

      // Products section animation
      if (productsSectionRef.current) {
        const productCarousels = document.querySelectorAll(
          '.product-images-carousel'
        );
        const carouselData = [];

        // Set up each carousel's animation logic
        productCarousels.forEach((carousel, carouselIndex) => {
          // Find which product this carousel belongs to
          const productContainer = carousel.closest('.rounded-lg');
          const productId = productContainer
            ? productContainer.getAttribute('data-product-id')
            : null;

          // Find control button
          const controlButton = carousel.querySelector('.carousel-control');

          const slides = carousel.querySelectorAll('.product-image-slide');
          const totalSlides = slides.length;

          if (totalSlides <= 1) {
            // Hide control button if there's only one slide
            if (controlButton) controlButton.style.display = 'none';
            return;
          }

          // Set all slides to be visible but positioned off-screen except the first one
          slides.forEach((slide, index) => {
            if (index === 0) {
              gsap.set(slide, { yPercent: 0 });
            } else {
              gsap.set(slide, { yPercent: 100 }); // Position below
            }
          });

          let currentIndex = 0;
          let animating = false;
          let isPaused = false;

          // Find progress indicator
          const progressIndicator = carousel.querySelector(
            '.carousel-progress-indicator'
          );

          // Calculate the circumference of the circle
          const radius = 15.5; // Same as the r attribute in the SVG
          const circumference = 2 * Math.PI * radius;

          // Track slide intervals for this carousel
          let slideInterval = null;
          const slideTransitionDelay = 3000; // 5 seconds between slides

          // Set up progress animation
          let progressAnimation;
          const startProgressAnimation = () => {
            if (progressAnimation) {
              progressAnimation.kill();
            }

            if (progressIndicator) {
              gsap.set(progressIndicator, { strokeDashoffset: circumference });
              progressAnimation = gsap.to(progressIndicator, {
                strokeDashoffset: 0,
                duration: slideTransitionDelay / 1000, // Convert ms to seconds
                ease: 'linear',
              });
            }
          };

          // Function to start both the interval and progress animation together
          const startCarousel = () => {
            if (slideInterval) {
              clearInterval(slideInterval);
            }

            // Start fresh with both the interval timer and progress animation
            slideInterval = setInterval(
              animateToNextSlide,
              slideTransitionDelay
            );
            startProgressAnimation();

            return slideInterval;
          };

          // Function to stop both the interval and progress animation
          const stopCarousel = () => {
            if (slideInterval) {
              clearInterval(slideInterval);
              slideInterval = null;
            }

            if (progressAnimation) {
              progressAnimation.pause();
            }
          };

          // Set up play/pause button
          if (controlButton) {
            controlButton.addEventListener('click', () => {
              isPaused = !isPaused;

              const playIcon = controlButton.querySelector('.play-icon');
              const pauseIcon = controlButton.querySelector('.pause-icon');

              if (isPaused) {
                controlButton.setAttribute('data-state', 'paused');
                pauseIcon.style.display = 'none';
                playIcon.style.display = 'block';

                // Stop both the timer and progress animation
                stopCarousel();
              } else {
                controlButton.setAttribute('data-state', 'playing');
                pauseIcon.style.display = 'block';
                playIcon.style.display = 'none';

                // Start both the timer and progress animation in sync
                startCarousel();
              }
            });
          }

          const animateToNextSlide = () => {
            if (animating || isPaused) return;
            animating = true;

            const nextIndex = (currentIndex + 1) % totalSlides;

            // Customize direction logic based on product ID or carousel index
            let direction;
            if (productId === 'mcp-registry' || carouselIndex === 0) {
              direction = nextIndex === 0 ? -1 : 1;
            } else if (productId === 'embedded-ipaas' || carouselIndex === 1) {
              direction = nextIndex === 1 ? -1 : 1;
            } else {
              direction = nextIndex === 0 ? -1 : 1;
            }

            // Current slide moves out
            gsap.to(slides[currentIndex], {
              yPercent: direction * -100, // Move up or down based on direction
              duration: 0.8,
              ease: 'power1.inOut',
            });

            // Next slide moves in
            gsap.fromTo(
              slides[nextIndex],
              {
                yPercent: direction * 100, // Start from below or above based on direction
              },
              {
                yPercent: 0, // Move to center
                duration: 0.8,
                ease: 'power1.inOut',
                onComplete: () => {
                  currentIndex = nextIndex;
                  animating = false;

                  // Reset and restart progress animation
                  if (!isPaused) {
                    startProgressAnimation();
                  }
                },
              }
            );
          };

          // Store the carousel control functions
          carouselData.push({
            animate: animateToNextSlide,
            interval: null,
            startCarousel: startCarousel,
            stopCarousel: stopCarousel,
            isPaused: isPaused,
          });
        });

        // Create a single ScrollTrigger for the entire products section
        if (carouselData.length > 0) {
          ScrollTrigger.create({
            trigger: productsSectionRef.current,
            start: 'top 80%',
            onEnter: () => {
              // Start all carousel animations
              carouselData.forEach(data => {
                if (!data.isPaused) {
                  // Use the startCarousel function that handles both interval and progress
                  data.interval = data.startCarousel();
                }
              });
            },
            onLeave: () => {
              // Stop all carousel animations when scrolling away
              carouselData.forEach(data => {
                data.stopCarousel();
                data.interval = null;
              });
            },
            onEnterBack: () => {
              // Restart animations when scrolling back
              carouselData.forEach(data => {
                if (!data.isPaused && !data.interval) {
                  // Use the startCarousel function that handles both interval and progress
                  data.interval = data.startCarousel();
                }
              });
            },
            onLeaveBack: () => {
              // Stop all carousel animations when scrolling back out
              carouselData.forEach(data => {
                data.stopCarousel();
                data.interval = null;
              });
            },
          });
        }
      }
    }, 200); // Slight delay to ensure DOM is ready

    // Cleanup
    return () => {
      clearTimeout(initTimeout);
      ScrollTrigger.getAll().forEach(st => st.kill());
    };
  }, []);

  return (
    <SiteLayout
      metaTitle={seoTitle}
      metaDescription={seoDescription}
      metaKeywords={seoKeywords}
      seoImage={seoImage && seoImage.url ? convertUrltoCdn(seoImage.url) : ''}
      location={location}
    >
      <section className="relative flex h-[600px] items-center justify-center overflow-hidden pt-28">
        {/* Full background Spline scene */}
        <div className="absolute inset-0 h-full w-full">
          <Spline scene="https://prod.spline.design/eFdPCYsz0qEwFhpC/scene.splinecode" />
        </div>

        {/* Content */}
        <Container className="relative z-10">
          <div className="flex flex-col items-center justify-center gap-6">
            {/* Blurred background div */}
            <div className="absolute inset-0 -z-10 flex items-center justify-center">
              <div className="h-[600px] w-[400px] rounded-[500px] bg-white blur-3xl"></div>
            </div>

            <h1 className="mb-0 text-center text-black text-h2 sm:text-h1">
              Build AI Agents <br />
              Fast
            </h1>
            <p className="mb-0 text-center text-xl font-medium text-black">
              Securely connect your AI to APIs, <br />
              MCPs, data, and more.
            </p>
            <div className="relative z-20">
              <ABookDemoBtn type="primary" />
            </div>
          </div>
        </Container>
      </section>
      <section className="overflow-hidden py-10 sm:py-14">
        <APartners className="w-full" gradientColor="var(--white)" />
      </section>
      <section className="overflow-hidden py-10 sm:py-20">
        <Container>
          <h2 className="mb-8 text-center text-black text-h2-xs sm:mb-14 sm:text-h2">
            One Platform. <br />
            Three Ways to Build.
          </h2>
          <div className="hidden sm:grid sm:grid-cols-3 sm:gap-16">
            {waysToBuild.map(build => {
              return (
                <div className="flex flex-col items-center" key={build.name}>
                  <img
                    className="mb-6 h-12 w-auto"
                    src={build.icon}
                    alt={build.name}
                  />
                  <h6 className="mb-2 text-center !font-semibold text-azure-950 text-h6">
                    {build.name}
                  </h6>
                  <p className="mb-0 whitespace-pre-wrap text-center !font-normal text-black text-label">
                    {build.excerpt}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="block sm:hidden">
            <Swiper
              className="!overflow-visible"
              slidesPerView={2.4}
              spaceBetween={24}
              breakpoints={{
                768: {
                  slidesPerView: 2.4,
                  spaceBetween: 24,
                },
                320: {
                  slidesPerView: 1.1,
                  spaceBetween: 16,
                },
              }}
            >
              {waysToBuild.map(build => {
                return (
                  <SwiperSlide key={build.name} className="!h-auto">
                    <div className="flex flex-col items-center rounded-lg border border-black/10 p-6">
                      <img
                        className="mb-6 h-12 w-auto"
                        src={build.icon}
                        alt={build.name}
                      />
                      <h6 className="mb-2 text-center !font-semibold text-azure-950 text-h6">
                        {build.name}
                      </h6>
                      <p className="mb-0 whitespace-pre-wrap text-center !font-normal text-black text-label">
                        {build.excerpt}
                      </p>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </Container>
      </section>
      <section className="overflow-hidden py-10 sm:py-20">
        <Container>
          <h2 className="mb-4 text-center text-black text-h2-xs sm:text-h2">
            Common Use Cases
          </h2>
          <p className="mb-8 text-center text-xl text-black sm:mb-20">
            Build the integrations you need to power core features in <br />
            your product and enterprise-grade agents.
          </p>
          <div className="relative hidden sm:block">
            <div className="grid grid-cols-3">
              {commonUseCases.map(useCase => {
                return (
                  <div
                    className="relative flex h-[477px] items-end overflow-hidden first:border-r first:border-r-black/10 last:border-l last:border-l-black/10"
                    key={useCase.name}
                  >
                    <img
                      className="h-auto w-full"
                      src={useCase.icon}
                      alt={useCase.name}
                    />
                    <div className="absolute left-0 top-0 p-8">
                      <h4 className="mb-2 !font-semibold -tracking-[0.28px] text-azure-950 text-h4">
                        {useCase.name}
                      </h4>
                      <p className="mb-0 w-3/4 whitespace-pre-wrap !font-normal text-black text-label">
                        {useCase.excerpt}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <span className="absolute -left-48 -right-48 top-0 z-10 h-px bg-black/10" />
            <span className="absolute -left-48 -right-48 top-[170px] z-10 h-px bg-black/10" />
            <span className="absolute -left-48 -right-48 bottom-0 z-10 h-px bg-black/10" />
            <span className="absolute -bottom-24 -top-24 left-0 z-10 w-px bg-black/10" />
            <span className="absolute -bottom-24 -top-24 right-0 z-10 w-px bg-black/10" />
          </div>
          <div className="block sm:hidden">
            <Swiper
              className="!overflow-visible"
              slidesPerView={2.4}
              spaceBetween={24}
              breakpoints={{
                768: {
                  slidesPerView: 2.4,
                  spaceBetween: 24,
                },
                320: {
                  slidesPerView: 1.1,
                  spaceBetween: 16,
                },
              }}
            >
              {commonUseCases.map(useCase => {
                return (
                  <SwiperSlide key={useCase.name} className="!h-auto">
                    <div className="relative flex h-[446px] items-end overflow-hidden rounded-lg border border-black/10">
                      <img
                        className="h-auto w-full"
                        src={useCase.icon}
                        alt={useCase.name}
                      />
                      <div className="absolute left-0 top-0 border-b border-b-black/10 p-6">
                        <h4 className="mb-2 !font-semibold -tracking-[0.28px] text-azure-950 text-h4">
                          {useCase.name}
                        </h4>
                        <p className="mb-0 w-3/4 whitespace-pre-wrap !font-normal text-black text-label">
                          {useCase.excerpt}
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </Container>
      </section>
      <section
        className={classNames(
          'relative z-[1] overflow-hidden py-10 sm:py-20',
          "after:absolute after:bottom-0 after:left-1/2 after:-z-[2] after:h-[856px] after:w-[2819px] after:-translate-x-1/2 after:bg-[url('https://cdn.runalloy.com/landing/2025/home/widget-bg-1.webp')] after:bg-cover after:bg-no-repeat",
          "before:absolute before:bottom-0 before:left-0 before:right-0 before:-z-[1] before:h-36 before:w-full before:bg-[url('https://cdn.runalloy.com/landing/2025/home/carousel-bg.webp')] before:bg-auto before:bg-repeat before:mix-blend-multiply before:sm:h-[432px]"
        )}
      >
        <Container>
          <div className="flex flex-col items-center">
            <h2 className="mb-6 text-center text-black text-h2-xs sm:text-h2">
              Stop Building Integrations <br />
              From Scratch
            </h2>
            <p className="mb-8 text-center text-xl font-light text-black/80">
              Alloy Automation is a powerful agentic toolkit for your product,
              with <br />
              embedded iPaaS and MCP technology built for product and dev
              leaders. We power your product’s <br />
              marketplace and provide the secure infrastructure you need for{' '}
              <br />
              Agentic AI to take meaningful action.
            </p>
            <AButton type="primary" href="/platform/mcp/">
              Explore MCP
            </AButton>
          </div>
          <div className="mt-16">
            <TabGroup
              selectedIndex={selectedAgenticAIData}
              onChange={setSelectedAgenticAIData}
            >
              <TabList className="sm:overflow-[unset] flex items-center justify-start gap-4 overflow-auto sm:justify-center sm:gap-6">
                {agenticAIData.map(({ name }, i) => (
                  <Tab
                    key={name}
                    className={classNames(
                      'relative w-1/2 min-w-max overflow-hidden text-ellipsis whitespace-nowrap border-b border-b-black/20 px-0 pb-4 text-left text-sm font-medium text-black/40 outline-none transition-smooth sm:min-w-[unset] sm:text-base',
                      {
                        '!border-b-black !text-black':
                          selectedAgenticAIData === i,
                      }
                    )}
                  >
                    {name}
                  </Tab>
                ))}
              </TabList>
              <TabPanels className="mt-6 sm:mt-16">
                {agenticAIData.map(
                  ({ name, chatFlow, integrations, tools }, i) => (
                    <TabPanel key={name}>
                      <Widget
                        type="dark"
                        chatFlow={chatFlow}
                        integrations={integrations}
                        tools={tools}
                        gsap={gsap}
                        ScrollTrigger={ScrollTrigger}
                      />
                    </TabPanel>
                  )
                )}
              </TabPanels>
            </TabGroup>
          </div>
        </Container>
      </section>
      <section
        className="overflow-hidden py-10 sm:py-20"
        ref={productsSectionRef}
      >
        <Container>
          <div className="mb-8 flex flex-col items-center sm:mb-20">
            <p className="mb-6 text-center text-xl font-semibold text-black/80">
              One Platform. Three Ways to Connect.
            </p>
            <h2 className="mb-0 text-center text-black text-h2-xs sm:text-h2">
              The Agentic Toolkit <br />
              For Your Product
            </h2>
          </div>
          <div className="flex flex-col gap-16">
            {products.map(product => {
              return (
                <div
                  className="flex flex-col-reverse gap-6 sm:grid sm:grid-cols-2"
                  key={product.id}
                >
                  <div className="p-0 sm:p-10">
                    <h3 className="mb-2 font-medium text-black text-h3">
                      {product.name}
                    </h3>
                    <p className="mb-10 text-base text-black/80">
                      {product.excerpt}
                    </p>
                    <p className="mb-10 text-base text-black/80">
                      {product.description}
                    </p>
                    <ul
                      className={classNames(
                        'mb-8 list-none space-y-2 text-base',
                        '[&_li]:relative [&_li]:pl-8 [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:top-0.5 [&_li]:before:h-5 [&_li]:before:w-5 [&_li]:before:bg-[url(https://cdn.runalloy.com/landing/2025/icons/icon-check.svg)] [&_li]:before:bg-contain [&_li]:before:bg-center [&_li]:before:bg-no-repeat'
                      )}
                    >
                      {product.features.map((feature, i) => {
                        return <li key={i}>{feature}</li>;
                      })}
                    </ul>
                    <ALink href={product.cta_link} type="primary">
                      {product.cta}
                    </ALink>
                  </div>
                  <div
                    className={classNames(
                      'relative h-[373px] w-full rounded-lg border border-black/10 sm:h-[518px]',
                      {
                        'bg-[#0B21A6]': product.id === 'mcp-registry',
                        'bg-azure-950': product.id === 'embedded-ipaas',
                        'bg-jade-700': product.id === 'connectivity-api',
                      }
                    )}
                    data-product-id={product.id}
                  >
                    <div className="product-images-carousel relative h-full w-full overflow-hidden">
                      <div className="carousel-control-wrapper absolute bottom-4 left-4 z-10 h-10 w-10">
                        <svg
                          className="carousel-progress absolute inset-0 h-full w-full"
                          viewBox="0 0 36 36"
                        >
                          <circle
                            className="carousel-progress-bg"
                            cx="18"
                            cy="18"
                            r="15.5"
                            fill="none"
                            stroke="rgba(255, 255, 255, 0.2)"
                            strokeWidth="2"
                          />
                          <circle
                            className="carousel-progress-indicator"
                            cx="18"
                            cy="18"
                            r="15.5"
                            fill="none"
                            stroke="rgba(255, 255, 255, 0.8)"
                            strokeWidth="2"
                            strokeDasharray="97.5"
                            strokeDashoffset="97.5"
                            transform="rotate(-90 18 18)"
                          />
                        </svg>
                        <button
                          className="carousel-control absolute inset-1 flex items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                          aria-label="Play/Pause carousel"
                          data-state="playing"
                        >
                          <svg
                            className="pause-icon"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ display: 'block' }}
                          >
                            <path d="M10 4H6V20H10V4Z" fill="currentColor" />
                            <path d="M18 4H14V20H18V4Z" fill="currentColor" />
                          </svg>
                          <svg
                            className="play-icon"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ display: 'none' }}
                          >
                            <path d="M6 4L19 12L6 20V4Z" fill="currentColor" />
                          </svg>
                        </button>
                      </div>
                      <div className="product-images-wrapper">
                        {product.images.map((img, i) => {
                          return (
                            <div
                              className={classNames(
                                'product-image-slide absolute h-full w-full',
                                {
                                  'border-y border-y-white/10': i === 1,
                                }
                              )}
                              key={`image-${i + 1}`}
                            >
                              <div className="flex h-full w-full items-center justify-center">
                                <img
                                  src={img}
                                  alt={`image-${i + 1}`}
                                  className="h-auto w-[340px] rounded-lg object-contain sm:w-[440px]"
                                />
                              </div>
                              <span className="absolute left-0 right-0 top-6 h-px bg-white/10 sm:top-[49px]" />
                              <span className="absolute bottom-6 left-0 right-0 h-px bg-white/10 sm:bottom-[49px]" />
                              <span className="absolute bottom-0 right-6 top-0 w-px bg-white/10 sm:right-[49px]" />
                              <span className="absolute bottom-0 left-6 top-0 w-px bg-white/10 sm:left-[49px]" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </section>
      <section className="overflow-x-clip py-10 sm:py-20">
        <Container>
          <h2 className="mb-8 text-black text-h2-xs sm:mb-20 sm:text-h2">
            Why Alloy Automation?
          </h2>
          <div ref={featuresContainerRef} className="relative hidden sm:block">
            <div className="rounded-lg border border-black/10">
              {whyAlloy.map(why => {
                return (
                  <div
                    className="flex h-[356px] flex-col gap-3 p-10 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-black/10"
                    key={why.name}
                  >
                    <div className="h-[42px] w-max">
                      <img
                        src={why.icon}
                        alt={why.name}
                        className="h-full w-auto"
                      />
                    </div>
                    <p className="mb-0 text-xl font-semibold -tracking-[0.22px] text-black">
                      {why.name}
                    </p>
                    <p className="text-base -tracking-[0.16px] text-black/80">
                      {why.description}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="absolute -bottom-8 -right-[272px] -top-8">
              <div className="sticky top-28 z-[1] flex h-[612px] w-[850px] items-center justify-center overflow-hidden rounded-lg border border-black/10 bg-white shadow-[0px_4px_28px_#1E1E200d]">
                <div className="flex h-full w-full items-center justify-center">
                  <img
                    ref={image1Ref}
                    src="https://cdn.runalloy.com/landing/2025/home/ai-first.webp"
                    alt="AI-First"
                    className="image-section-1 absolute left-0 top-0 h-full w-full object-cover object-left-top opacity-100 transition-opacity duration-300"
                  />
                  <img
                    ref={image2Ref}
                    src="https://cdn.runalloy.com/landing/2025/home/battle-tested-integration-infrastructure.webp"
                    alt="Battle-Tested Infrastructure"
                    className="image-section-2 absolute left-0 top-0 h-full w-full object-cover object-left-top opacity-0 transition-opacity duration-300"
                  />
                  <img
                    ref={image3Ref}
                    src="https://cdn.runalloy.com/landing/2025/home/enterprise-grade.webp"
                    alt="Enterprise-grade"
                    className="image-section-3 absolute left-0 top-0 h-full w-full object-cover object-left-top opacity-0 transition-opacity duration-300"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="block sm:hidden">
            <Swiper
              className="!overflow-visible"
              slidesPerView={2.4}
              spaceBetween={24}
              breakpoints={{
                768: {
                  slidesPerView: 2.4,
                  spaceBetween: 24,
                },
                320: {
                  slidesPerView: 1.1,
                  spaceBetween: 16,
                },
              }}
            >
              {whyAlloy.map(why => {
                return (
                  <SwiperSlide key={why.name} className="!h-auto">
                    <div className="flex h-full flex-col gap-4 overflow-hidden rounded-lg border border-black/10 p-6">
                      <div className="h-[42px] w-max">
                        <img
                          src={why.icon}
                          alt={why.name}
                          className="h-full w-auto"
                        />
                      </div>
                      <p className="mb-0 text-xl font-semibold -tracking-[0.22px] text-black">
                        {why.name}
                      </p>
                      <p className="text-base -tracking-[0.16px] text-black/80">
                        {why.description}
                      </p>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </Container>
      </section>
      <section
        className="hidden overflow-hidden py-10 sm:block sm:py-20"
        ref={statsSectionRef}
      >
        <Container>
          <div className="grid grid-cols-3 gap-6">
            <div className="relative z-[1] h-[440px] overflow-hidden rounded-lg border border-black/10 p-8 shadow-[0px_4px_12px_#1E1E200d]">
              <div className="flex h-full w-full flex-col justify-between">
                <div>
                  <h1 ref={percentARef} className="mb-2 text-azure-600 text-h1">
                    0%
                  </h1>
                  <p className="mb-0 text-base -tracking-[0.16px] text-black/80">
                    of software buyers say a software provider's ability to
                    provide integration support is their primary concern.
                  </p>
                </div>
                <div className="h-[30px]">
                  <img
                    src="https://cdn.runalloy.com/landing/2025/home/icons/gartner.webp"
                    alt="gartner"
                    className="h-full w-auto"
                  />
                </div>
              </div>
              <div
                ref={bgARef}
                className="absolute bottom-0 left-0 right-0 -z-[2] h-0 border-t border-t-azure-200 bg-azure-100"
              />
            </div>
            <div className="relative z-[1] h-[440px] overflow-hidden rounded-lg border border-black/10 p-8 shadow-[0px_4px_12px_#1E1E200d]">
              <div className="flex h-full w-full flex-col justify-between">
                <div>
                  <h1 ref={percentBRef} className="mb-2 text-black/80 text-h1">
                    0%
                  </h1>
                  <p className="mb-0 text-base -tracking-[0.16px] text-black/80">
                    of businesses view agentic AI as a <br />
                    competitive advantage
                  </p>
                </div>
                <div className="h-14">
                  <img
                    src="https://cdn.runalloy.com/landing/2025/home/icons/world-economic-forum.webp"
                    alt="salesforce"
                    className="h-full w-auto"
                  />
                </div>
              </div>
              <div
                ref={bgBRef}
                className="absolute bottom-0 left-0 right-0 -z-[2] h-0 border-t border-t-black/10 bg-black/10"
              />
            </div>
            <div className="relative z-[1] h-[440px] overflow-hidden rounded-lg border border-black/10 p-8 shadow-[0px_4px_12px_#1E1E200d]">
              <div className="flex h-full w-full flex-col justify-between">
                <div>
                  <h1 ref={percentCRef} className="mb-2 text-jade-400 text-h1">
                    0%
                  </h1>
                  <p className="mb-0 text-base -tracking-[0.16px] text-black/80">
                    percent of companies are now using gen AI in at least one
                    business function.
                  </p>
                </div>
                <div className="h-[44px]">
                  <img
                    src="https://cdn.runalloy.com/landing/2025/home/icons/mckinsey.webp"
                    alt="mckinsey"
                    className="h-full w-auto"
                  />
                </div>
              </div>
              <div
                ref={bgCRef}
                className="absolute bottom-0 left-0 right-0 -z-[2] h-0 border-t border-t-jade-200 bg-jade-100"
              />
            </div>
          </div>
        </Container>
      </section>
      <section className="overflow-hidden py-10 sm:py-20">
        <Container>
          <div className="flex flex-col items-center">
            <h2 className="mb-5 text-center text-black text-h2-xs sm:text-h2">
              Integration Library
            </h2>
            <p className="mb-5 text-center text-xl font-light text-black/80">
              Alloy Automation has hundreds of pre-built connectors and
              thousands of API endpoints.
            </p>
            <AButton type="primary" href="/integrations/">
              Explore connectors
            </AButton>
          </div>
        </Container>
        <AppCarousel className="mt-8 sm:mt-14" />
      </section>
      <G2Section />
      <section className="overflow-hidden py-10 sm:py-20">
        <Container>
          <div className="flex flex-col items-center justify-center">
            <h2 className="mb-5 text-center text-black text-h2-xs sm:text-h2">
              Trusted by the Best
            </h2>
            <p className="mb-0 text-balance text-center text-xl font-light text-black/80">
              Whether you’re a fast growing startup or one of the largest <br />
              commerce companies in the world, your data is <br />
              in good hands with Alloy Automation.
            </p>
          </div>
          <div className="my-14 hidden items-start gap-6 overflow-hidden rounded-none border border-transparent sm:flex sm:gap-0 sm:rounded-2xl sm:border-black/10">
            <Link
              to="/case-studies/buy-with-prime/"
              className="w-1/2 border-r border-r-black/10 transition-smooth hover:bg-black/2"
            >
              <div className="relative flex h-[184px] w-full items-center justify-center">
                <img
                  src="https://cdn.runalloy.com/landing/2025/case-studies/amazon-bg.webp"
                  alt="amazon-bg"
                  className="absolute inset-0 -z-[1] h-full w-full object-cover transition-smooth group-hover:scale-105"
                />
                <img
                  src="https://cdn.runalloy.com/landing/2025/case-studies/amazon.svg"
                  alt="amazon"
                />
              </div>
              <div className="flex items-center justify-center p-8">
                <p className="mb-0 text-center text-2xl font-semibold text-azure-950">
                  20+ Integrations Launched <br />
                  in under 6 weeks
                </p>
              </div>
              <div className="flex items-center justify-center border-y border-y-black/10 p-12 sm:min-h-[238px]">
                <p className="mb-0 text-center text-xl text-black">
                  “Alloy Automation helps automate key business functions that
                  save time by giving you a way to manage all data in one
                  place.”
                </p>
              </div>
              <div className="flex items-center justify-center p-8">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 overflow-hidden rounded border border-black/10">
                    <img
                      src={convertUrltoCdn(
                        'https://alloy-assets.s3.amazonaws.com/landing/uploads-new/Russell_Baker_Headshot_f94e3c37cf.jpeg'
                      )}
                      alt="Russel Baker"
                      className="h-full w-full object-cover grayscale"
                    />
                  </div>
                  <div>
                    <p className="mb-0 text-base leading-[140%] text-black">
                      Russel Baker
                    </p>
                    <p className="mb-0 text-base leading-[140%] text-black">
                      Director of Partnerships and Integrations
                    </p>
                  </div>
                </div>
              </div>
            </Link>
            <Link
              to="/case-studies/typeform/"
              className="w-1/2 transition-smooth hover:bg-black/2"
            >
              <div className="relative flex h-[184px] w-full items-center justify-center">
                <img
                  src="https://cdn.runalloy.com/landing/2025/case-studies/typeform-bg-2.webp"
                  alt="typeform-bg"
                  className="absolute inset-0 -z-[1] h-full w-full object-cover transition-smooth group-hover:scale-105"
                />
                <img
                  src="https://cdn.runalloy.com/landing/2025/case-studies/typeform-2.svg"
                  alt="typeform"
                />
              </div>
              <div className="flex items-center justify-center p-8">
                <p className="mb-0 text-center text-2xl font-semibold text-azure-950">
                  60K+ Typeform customers use <br />
                  these integrations
                </p>
              </div>
              <div className="flex items-center justify-center border-y border-y-black/10 p-12 sm:min-h-[238px]">
                <p className="mb-0 text-center text-xl text-black">
                  “Almost all of our customers rely on integrations. With such a
                  varied customer base, we needed a vendor capable of supporting
                  a wide range of integrations to meet our customers’ unique
                  needs.”
                </p>
              </div>
              <div className="flex items-center justify-center p-8">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 overflow-hidden rounded border border-black/10">
                    <img
                      src={convertUrltoCdn(
                        'https://alloy-assets.s3.us-east-1.amazonaws.com/landing/uploads-new/Elsa_Heffernan_56ab242fb0.jpeg'
                      )}
                      alt="Elsa Heffernan"
                      className="h-full w-full object-cover grayscale"
                    />
                  </div>
                  <div>
                    <p className="mb-0 text-base leading-[140%] text-black">
                      Elsa Heffernan
                    </p>
                    <p className="mb-0 text-base leading-[140%] text-black">
                      Senior Product Manager
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
          <div className="mt-8">
            <Swiper
              className="!overflow-visible"
              slidesPerView={2.4}
              spaceBetween={24}
              breakpoints={{
                768: {
                  slidesPerView: 2.4,
                  spaceBetween: 24,
                },
                320: {
                  slidesPerView: 1.1,
                  spaceBetween: 16,
                },
              }}
            >
              <SwiperSlide className="!h-auto sm:!hidden">
                <div className="h-full overflow-hidden rounded-2xl border border-black/10">
                  <div className="relative flex h-48 w-full items-center justify-center">
                    <img
                      src="https://cdn.runalloy.com/landing/2025/case-studies/amazon-bg.webp"
                      alt="amazon-bg"
                      className="absolute inset-0 -z-[1] h-full w-full object-cover transition-smooth group-hover:scale-105"
                    />
                    <img
                      src="https://cdn.runalloy.com/landing/2025/case-studies/amazon.svg"
                      alt="amazon"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <p className="mb-6 text-base text-black">
                      Alloy powers integrations for 55% of Amazon Buy with Prime
                      merchants freeing up hundreds of hours.
                    </p>
                    <ALink href="/case-studies/amazon/">
                      Read Buy with Prime's story
                    </ALink>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide className="!h-auto sm:!hidden">
                <div className="h-full overflow-hidden rounded-2xl border border-black/10">
                  <div className="relative flex h-48 w-full items-center justify-center">
                    <img
                      src="https://cdn.runalloy.com/landing/2025/case-studies/typeform-bg-2.webp"
                      alt="typeform-bg"
                      className="absolute inset-0 -z-[1] h-full w-full object-cover transition-smooth group-hover:scale-105"
                    />
                    <img
                      src="https://cdn.runalloy.com/landing/2025/case-studies/typeform-2.svg"
                      alt="typeform"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <p className="mb-6 text-base text-black sm:w-3/4">
                      How Typeform implemented an end-to-end white labeled
                      experience with Alloy’s Headless APIs.
                    </p>
                    <ALink href="/case-studies/typeform/">
                      Read Typeform's story
                    </ALink>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide className="!h-auto">
                <div className="h-full overflow-hidden rounded-2xl border border-black/10">
                  <div className="relative flex h-48 w-full items-center justify-center">
                    <img
                      src="https://cdn.runalloy.com/landing/2025/case-studies/best-buy-bg.webp"
                      alt="best-buy-bg"
                      className="absolute inset-0 -z-[1] h-full w-full object-cover transition-smooth group-hover:scale-105"
                    />
                    <img
                      src="https://cdn.runalloy.com/landing/2025/case-studies/best-buy.svg"
                      alt="best-buy"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <p className="text- mb-6 line-clamp-3 text-base">
                      Teaming up with Alloy gives us the opportunity to improve
                      the turnaround time for Best Buy's partners. Alloy's
                      integration solution allows us to create an end-to-end
                      BOPIS experience for our partners in a few short days.
                    </p>
                    <ALink href="/case-studies/best-buy/">
                      Read Best Buy's story
                    </ALink>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide className="!h-auto">
                <div className="h-full overflow-hidden rounded-2xl border border-black/10">
                  <div className="relative flex h-48 w-full items-center justify-center">
                    <img
                      src="https://cdn.runalloy.com/landing/2025/case-studies/burberry-bg.webp"
                      alt="burberry-bg"
                      className="absolute inset-0 -z-[1] h-full w-full object-cover transition-smooth group-hover:scale-105"
                    />
                    <img
                      src="https://cdn.runalloy.com/landing/2025/case-studies/burberry.svg"
                      alt="burberry"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <p className="mb-6 line-clamp-3 text-base text-black sm:w-3/4">
                      Burberry saved millions by automating key processes across
                      their physical stores and logistics operations.
                    </p>
                    <ALink href="/case-studies/burberry/">
                      Read Burberry's story
                    </ALink>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide className="!h-auto">
                <div className="h-full overflow-hidden rounded-2xl border border-black/10">
                  <div className="relative flex h-48 w-full items-center justify-center">
                    <img
                      src="https://cdn.runalloy.com/landing/2025/case-studies/postscript-bg.webp"
                      alt="postscript-bg"
                      className="absolute inset-0 -z-[1] h-full w-full object-cover transition-smooth group-hover:scale-105"
                    />
                    <img
                      src="https://cdn.runalloy.com/landing/2025/case-studies/postscript-2.svg"
                      alt="postscript"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <p className="mb-6 text-base text-black sm:w-2/3">
                      Postscript rolls out integrations 1.5X faster with Alloy.
                    </p>
                    <ALink href="/case-studies/postscript/">
                      Read Postscript's story
                    </ALink>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
        </Container>
      </section>
      <CustomCTA title="Get the Agentic Toolkit your product needs." />
    </SiteLayout>
  );
};

export default Home;

export const pageQuery = graphql`
  query {
    strapiHomePage {
      seo_title
      seo_description
      seo_keywords
      seo_image {
        url
      }
      announcement {
        show
        title
        link
      }
    }
  }
`;
